'use client';

import { type ReactNode, useMemo } from 'react';
import { clsx } from 'clsx';
import { ImageLoadingStatus, useImageLoader } from '../../util/image';
import { type Interaction, Interactive } from '../../util/interaction';
import { VerticalStack } from '../alignment/vertical-stack';
import { Skeleton } from '../loading/skeleton';
import styles from './avatar.module.scss';

export type AvatarVariant = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'danger' | 'neutral' | 'neutral-lt' | 'dark' | 'green' | 'image';

export type AvatarSize = 'small' | 'medium';

function getCharactersFromName(name: string | undefined) {
  if (!name) {
    return '';
  }

  const nameParts = name.split(' ');

  const partsToUse: string[] = [nameParts[0]?.charAt(0).toUpperCase()];

  if (nameParts.length > 1) {
    partsToUse.push(nameParts[nameParts.length - 1].charAt(0).toUpperCase());
  }

  return partsToUse.filter(Boolean).join('');
}

function getVariant(
  specifiedVariant: AvatarVariant | undefined,
  hasIcon: boolean,
  fileSrc: string | undefined,
  imageStatus: ImageLoadingStatus,
): AvatarVariant {
  if ((fileSrc && imageStatus !== ImageLoadingStatus.Error) || (hasIcon && !specifiedVariant)) {
    return 'image';
  }

  return specifiedVariant || 'primary';
}

export interface BaseAvatar {
  icon?: ReactNode;
  interaction?: Interaction;
  fileSrc?: string;
  name: string | undefined;
}

export interface AvatarProps extends BaseAvatar {
  className?: string;
  size?: AvatarSize;
  variant?: Exclude<AvatarVariant, 'image'>; // automatically made image if fileSrc is provided and the image can be loaded
}

export function Avatar({ className, icon, interaction, fileSrc, name, size = 'medium', variant: specifiedVariant }: AvatarProps) {
  const hasIcon = Boolean(icon);
  const hasNonImageContentReady = Boolean(name || hasIcon);
  const imageStatus = useImageLoader(fileSrc);
  const { showLoading, variant } = useMemo(() => {
    const showVariant = getVariant(specifiedVariant, hasIcon, fileSrc, imageStatus);

    if (!hasIcon && showVariant === 'image' && imageStatus === ImageLoadingStatus.Loading) {
      return {
        showLoading: true,
        variant: 'image',
      };
    }

    return {
      showLoading: !hasNonImageContentReady,
      variant: showVariant,
    };
  }, [imageStatus, hasNonImageContentReady, hasIcon, fileSrc, specifiedVariant]);

  const baseClassName = clsx(styles.avatar, styles[`size-${size}`], className);

  if (showLoading) {
    return <Skeleton parentClassName={baseClassName} type="rect" />;
  }

  const base = (
    <VerticalStack align="center" className={clsx(baseClassName, styles[`variant-${variant}`])} inlineAlign="center">
      {!hasIcon && variant === 'image' ? <img alt={name} src={fileSrc} /> : icon || <span>{getCharactersFromName(name)}</span>}
    </VerticalStack>
  );

  return interaction ? <Interactive {...interaction}>{base}</Interactive> : base;
}
